import type {Content, KeyTextField} from '@prismicio/client'
import {isFilled} from '@prismicio/client'
import type {SliceComponentProps} from '@prismicio/react'
import {useRouter} from 'next/router'
import {useLinkResolver} from 'prismicio'
import {useMemo} from 'react'
import {DEFAULT_LOCALE} from 'src/common/constants'
import {getButtonsData} from 'src/common/helpers'
import {logger} from 'src/common/logger'
import type {Context} from 'src/common/types'
import {DEFAULT_CURRENCY} from './constants'
import {downloadReportPdf} from './reportPdf'
import {RoiCalculator as RoiCalculatorUI} from './RoiCalculator'
import {getTranslations} from './translations'
import type {CurrencyCode, PrimaryButton, Report} from './types'

export type RoiCalculatorProps = SliceComponentProps<Content.RoiCalculatorSlice>

const textOrEmpty = (field: KeyTextField): string => (isFilled.keyText(field) ? field : '')

const CURRENCY_CODES: Record<Content.RoiCalculatorSlice['primary']['currency'], CurrencyCode> = {
  Euro: 'EUR',
  Pound: 'GBP',
}

const RoiCalculator = ({
  slice,
  context: {pageUid, buttonsData},
}: RoiCalculatorProps & {context: Context}): JSX.Element => {
  const {locale = DEFAULT_LOCALE} = useRouter()
  const {linkResolver} = useLinkResolver()
  const {labels, financeStackOptions} = useMemo(() => getTranslations(locale), [locale])
  const {primaryButtonData} = getButtonsData({buttonsData, primaryButton: slice.primary.primary_button})
  const heading = textOrEmpty(slice.primary.heading)
  const disclaimer = textOrEmpty(slice.primary.disclaimer)
  const currency = CURRENCY_CODES[slice.primary.currency] ?? DEFAULT_CURRENCY

  const primaryButton: PrimaryButton | null =
    primaryButtonData?.link && primaryButtonData.label
      ? {
          label: primaryButtonData.label,
          href: linkResolver(primaryButtonData.link, 'roi-calculator'),
          marketingId: primaryButtonData.marketing_id ?? '',
        }
      : null

  const shareReport = (report: Report, captureElement: HTMLElement | null) => {
    if (!captureElement) {
      return
    }

    downloadReportPdf(
      report,
      {locale, currency, title: heading, disclaimer, labels, financeStackOptions},
      captureElement,
    ).catch((error) => logger.error('Error downloading the ROI report', undefined, error))
  }

  return (
    <RoiCalculatorUI
      locale={locale}
      currency={currency}
      eyebrow={textOrEmpty(slice.primary.eyebrow)}
      heading={heading}
      description={textOrEmpty(slice.primary.description)}
      disclaimer={disclaimer}
      labels={labels}
      financeStackOptions={financeStackOptions}
      primaryButton={primaryButton}
      pageUid={pageUid ?? ''}
      backgroundColor={slice.primary.background_color}
      paddingTop={slice.primary.padding_top}
      paddingBottom={slice.primary.padding_bottom}
      onShare={shareReport}
    />
  )
}

export default RoiCalculator
